import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import CustomReportsPanel from '../features/custom-reports/components/CustomReportsPanel';
import ReportConfigModal from '../features/sessions/report/components/ReportConfigModal';
import EditEventModal from '../features/events/components/EditEventModal';
import EventDetailHeader from '../features/events/components/EventDetailHeader';
import EventSessionsTab from '../features/events/components/EventSessionsTab';
import EventStatisticsTab from '../features/events/components/EventStatisticsTab';
import EventSummaryStats from '../features/events/components/EventSummaryStats';
import { useEventDetail } from '../features/events/hooks/useEventDetail';
import { useEventReportStatus } from '../features/events/hooks/useEventReportStatus';
import '../features/events/styles/event-detail.css';

const TABS = [
  { id: 'sessions', label: 'Sessions' },
  { id: 'statistics', label: 'Statistics' },
  { id: 'report', label: 'Report' },
  { id: 'in-depth', label: 'In-depth report' },
];

export default function EventDetailPage() {
  const { id } = useParams();
  const { event, sessions, loadingSessions, reloadEvent, saveEvent } = useEventDetail(id);
  const statistics = useEventReportStatus(id);
  const [activeTab, setActiveTab] = useState('sessions');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);

  return (
    <div className="event-detail-page">
      <div className="event-detail-content">
        <Link to="/events" className="event-back-link">
          <ArrowLeftIcon /> Back to events
        </Link>

        <EventDetailHeader
          event={event}
          onEdit={() => setIsEditOpen(true)}
          onDefaults={() => setDefaultsOpen(true)}
        />
        <EventSummaryStats event={event} sessions={sessions} />

        <div className="event-detail-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={activeTab === tab.id ? 'active' : ''}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {tab.id === 'sessions' && <span>{sessions.length}</span>}
            </button>
          ))}
        </div>

        {activeTab === 'sessions' && <EventSessionsTab sessions={sessions} loading={loadingSessions} />}
        {activeTab === 'statistics' && (
          <EventStatisticsTab event={event} eventId={id} statistics={statistics} onPublished={reloadEvent} />
        )}
        {activeTab === 'report' && (
          <CustomReportsPanel scope="event" scopeId={id} title={event?.title} sessions={sessions} missions={Boolean(event?.missions)} />
        )}
        {activeTab === 'in-depth' && (
          <CustomReportsPanel scope="event" scopeId={id} title={event?.title} sessions={sessions} mode="event_synthesis" />
        )}
      </div>

      <EditEventModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSave={saveEvent}
        event={event}
      />
      <ReportConfigModal
        isOpen={defaultsOpen}
        onClose={() => setDefaultsOpen(false)}
        initialSettings={event?.report_settings}
        title="Default report settings"
        submitLabel="Save defaults"
        intro="New sessions of this event start with these options. You can still change them on a session when its report is generated."
        onSubmit={async (settings) => {
          await saveEvent({ report_settings: settings });
          setDefaultsOpen(false);
        }}
      />
    </div>
  );
}
